

// --- CONFIGURATION ---

// OpenAI API Key is expected to be in your .env file
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
const GOOGLE_VISION_API_KEY = import.meta.env.VITE_GOOGLE_VISION_API_KEY || import.meta.env.GOOGLE_VISION_API_KEY;
const AZURE_CONTENT_MODERATOR_ENDPOINT = import.meta.env.VITE_AZURE_CONTENT_MODERATOR_ENDPOINT || import.meta.env.AZURE_CONTENT_MODERATOR_ENDPOINT;
const AZURE_CONTENT_MODERATOR_KEY = import.meta.env.VITE_AZURE_CONTENT_MODERATOR_KEY || import.meta.env.AZURE_CONTENT_MODERATOR_KEY;

export type ModerationCategory = 'sexual' | 'profanity' | 'hate' | 'links' | 'violence' | 'self-harm' | 'adult' | 'racy' | 'medical' | 'spoof';

export interface ModerationMatch {
  category: ModerationCategory;
  keyword: string;
  score?: number;
}

// --- LOCAL KEYWORD & REGEX-BASED FILTERING (First Pass) ---

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\\]/g, '\\$&');
const normalize = (value: string): string => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const sexualKeywords = ['onlyfans', 'porn', 'porno', 'desnudez', 'nudity', 'orgía', 'incesto', 'xxx', 'hentai'];
const profanityKeywords = ['fuck', 'shit', 'bitch', 'puta', 'puto', 'mierda', 'cabrón', 'cabrona', 'gilipollas', 'pendejo', 'zorra', 'capullo', 'imbécil', 'idiota', 'estúpido', 'mamón', 'verga', 'coño', 'joder', 'hostia', 'chinga', 'culero'];
const hateSpeechKeywords = ['nigger', 'nigga', 'chink', 'spic', 'sudaca', 'negrata', 'maricón', 'maricon', 'faggot', 'machirulo', 'feminazi', 'retard', 'subnormal', 'mogólico'];
const violenceKeywords = ['suicídate', 'suicidate', 'muérete', 'muerete', 'matate', 'mátate', 'te voy a matar', 'kill yourself', 'die'];
const urlRegex = /https?:\/\/[^\s/$.?#].[^\s]*/i;

interface KeywordPattern {
  original: string;
  category: ModerationCategory;
  regex: RegExp;
}

const buildPatterns = (keywords: string[], category: ModerationCategory): KeywordPattern[] =>
  keywords.map((keyword) => {
    const normalized = normalize(keyword.trim());
    const pattern = escapeRegExp(normalized).replace(/\s+/g, '\\s+');
    return {
      original: keyword,
      category,
      regex: new RegExp(`\\b${pattern}\\b`, 'i'),
    };
  });

const localModerationPatterns: KeywordPattern[] = [
  ...buildPatterns(sexualKeywords, 'sexual'),
  ...buildPatterns(profanityKeywords, 'profanity'),
  ...buildPatterns(hateSpeechKeywords, 'hate'),
  ...buildPatterns(violenceKeywords, 'violence'),
];

const detectLocalMatches = (normalizedText: string): ModerationMatch[] => {
  const matches: ModerationMatch[] = [];
  localModerationPatterns.forEach(({ regex, category, original }) => {
    if (regex.test(normalizedText)) {
      matches.push({ category, keyword: original });
    }
  });
  if (urlRegex.test(normalizedText)) {
    matches.push({ category: 'links', keyword: 'URL/Link' });
  }
  return matches;
};

// --- OPENAI API INTEGRATION (Second Pass) ---

interface OpenAIModerationResult {
  flagged: boolean;
  categories: {
    'hate': boolean;
    'hate/threatening': boolean;
    'self-harm': boolean;
    'sexual': boolean;
    'sexual/minors': boolean;
    'violence': boolean;
    'violence/graphic': boolean;
  };
  category_scores: Record<string, number>;
}

const callOpenAIModeration = async (text: string): Promise<ModerationMatch[]> => {
  if (!OPENAI_API_KEY) {
    console.warn('[moderation] VITE_OPENAI_API_KEY is not set. Skipping OpenAI moderation.');
    return [];
  }
  if (!text) {
    return [];
  }

  try {
    const response = await fetch('https://api.openai.com/v1/moderations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({ input: text }),
    });

    if (!response.ok) {
      const errorBody = await response.json();
      console.error('[moderation] OpenAI API error:', errorBody);
      // Fail open (allow content) if the API call fails
      return [];
    }

    const { results } = (await response.json()) as { results: OpenAIModerationResult[] };
    const result = results[0];
    const matches: ModerationMatch[] = [];

    if (result.flagged) {
      if (result.categories['hate'] || result.categories['hate/threatening']) {
        matches.push({ category: 'hate', keyword: 'OpenAI-detected' });
      }
      if (result.categories['sexual'] || result.categories['sexual/minors']) {
        matches.push({ category: 'sexual', keyword: 'OpenAI-detected' });
      }
      if (result.categories['violence'] || result.categories['violence/graphic']) {
        matches.push({ category: 'violence', keyword: 'OpenAI-detected' });
      }
      if (result.categories['self-harm']) {
        matches.push({ category: 'self-harm', keyword: 'OpenAI-detected' });
      }
    }

    return matches;
  } catch (error) {
    console.error('[moderation] Failed to call OpenAI Moderation API:', error);
    // Fail open (allow content) if the fetch itself fails
    return [];
  }
};

// --- GOOGLE VISION API INTEGRATION (Image Moderation) ---

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const moderateImage = async (file: File): Promise<ModerationMatch[]> => {
  if (!GOOGLE_VISION_API_KEY) {
    console.warn('[moderation] GOOGLE_VISION_API_KEY is not set. Skipping Google Vision moderation.');
    return [];
  }

  try {
    const base64Image = await fileToBase64(file);
    const content = base64Image.split(',')[1]; // Remove data URL prefix

    const response = await fetch(`https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_VISION_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        requests: [
          {
            image: { content },
            features: [{ type: 'SAFE_SEARCH_DETECTION' }],
          },
        ],
      }),
    });

    if (!response.ok) {
      console.error('[moderation] Google Vision API error:', await response.text());
      return [];
    }

    const data = await response.json();
    const safeSearch = data.responses?.[0]?.safeSearchAnnotation;

    if (!safeSearch) return [];

    const matches: ModerationMatch[] = [];
    const likelihoods = ['LIKELY', 'VERY_LIKELY'];

    if (likelihoods.includes(safeSearch.adult)) matches.push({ category: 'adult', keyword: 'GoogleVision-Adult' });
    if (likelihoods.includes(safeSearch.violence)) matches.push({ category: 'violence', keyword: 'GoogleVision-Violence' });
    if (likelihoods.includes(safeSearch.racy)) matches.push({ category: 'racy', keyword: 'GoogleVision-Racy' });
    if (likelihoods.includes(safeSearch.medical)) matches.push({ category: 'medical', keyword: 'GoogleVision-Medical' });

    return matches;
  } catch (error) {
    console.error('[moderation] Failed to call Google Vision API:', error);
    return [];
  }
};

// --- AZURE CONTENT MODERATOR INTEGRATION (Text) ---

const callAzureModeration = async (text: string): Promise<ModerationMatch[]> => {
  if (!AZURE_CONTENT_MODERATOR_ENDPOINT || !AZURE_CONTENT_MODERATOR_KEY) {
    return [];
  }

  try {
    const url = `${AZURE_CONTENT_MODERATOR_ENDPOINT}/contentmoderator/moderate/v1.0/ProcessText/Screen?classify=true`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Ocp-Apim-Subscription-Key': AZURE_CONTENT_MODERATOR_KEY,
      },
      body: text,
    });

    if (!response.ok) {
      console.error('[moderation] Azure API error:', await response.text());
      return [];
    }

    const data = await response.json();
    const matches: ModerationMatch[] = [];

    // Check PII or Classification
    if (data.Classification) {
      const { Category1, Category2, Category3 } = data.Classification;
      // Category1: Sexually explicit
      if (Category1.Score > 0.5) matches.push({ category: 'sexual', keyword: 'Azure-Sexual', score: Category1.Score });
      // Category2: Sexually suggestive
      if (Category2.Score > 0.5) matches.push({ category: 'sexual', keyword: 'Azure-Suggestive', score: Category2.Score });
      // Category3: Offensive
      if (Category3.Score > 0.5) matches.push({ category: 'profanity', keyword: 'Azure-Offensive', score: Category3.Score });
    }

    return matches;
  } catch (error) {
    console.error('[moderation] Failed to call Azure Moderation API:', error);
    return [];
  }
};

// --- MAIN MODERATION LOGIC ---

export class ContentModerationError extends Error {
  code: 'CONTENT_BLOCKED';
  categories: ModerationCategory[];
  matches: ModerationMatch[];

  constructor(matches: ModerationMatch[]) {
    super('CONTENT_BLOCKED');
    this.name = 'ContentModerationError';
    this.code = 'CONTENT_BLOCKED';
    // Filter out 'links' category from blocking categories if it's not meant to block
    this.matches = matches;
    this.categories = Array.from(new Set(matches.map((match) => match.category)));
  }
}

const combineInputs = (inputs: Array<string | undefined | null>): string =>
  inputs.filter((value): value is string => typeof value === 'string' && value.trim().length > 0).join(' ');

export const assertContentIsAllowed = async (inputs: Array<string | undefined | null>) => {
  const combinedText = combineInputs(inputs);
  if (!combinedText) {
    return;
  }

  // 1. First pass: Local, fast checks
  const localMatches = detectLocalMatches(normalize(combinedText));
  // Filter out 'links' from local matches if they should not block
  const blockingLocalMatches = localMatches.filter(match => match.category !== 'links');
  if (blockingLocalMatches.length > 0) {
    console.log('[moderation] Blocked by local filter:', blockingLocalMatches);
    throw new ContentModerationError(blockingLocalMatches);
  }

  // 2. Second pass: Hybrid Cloud (OpenAI + Azure)
  // Run in parallel for better performance
  const [openAIMatches, azureMatches] = await Promise.all([
    callOpenAIModeration(combinedText),
    callAzureModeration(combinedText)
  ]);

  const allMatches = [...openAIMatches, ...azureMatches];

  if (allMatches.length > 0) {
    console.log('[moderation] Blocked by Cloud filters:', allMatches);
    throw new ContentModerationError(allMatches);
  }
};